import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import {
  JobItem,
  JobCreateRequest,
  JobUpdateRequest,
  JobsResponse,
} from "../../interfaces/Job";
import * as jobService from "../../services/jobService";

interface JobState {
  items: JobItem[];
  currentItem: JobItem | null;
  loading: boolean;
  error: string | null;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

const initialState: JobState = {
  items: [],
  currentItem: null,
  loading: false,
  error: null,
  pageNumber: 1,
  pageSize: 20,
  totalCount: 0,
  totalPages: 0,
};

// Async thunk to fetch jobs from API
export const fetchJobs = createAsyncThunk(
  "jobs/fetchJobs",
  async (params: {
    pageNumber?: number;
    pageSize?: number;
    clientId?: string;
    status?: number;
    search?: string;
    include?: string;
  } = {}) => {
    const {
      pageNumber = 1,
      pageSize = 20,
      clientId,
      status,
      search,
      include,
    } = params;
    const response: JobsResponse = await jobService.getJobs(
      pageNumber,
      pageSize,
      clientId,
      status,
      search,
      include
    );
    return response;
  }
);

// Async thunk to fetch job by ID
export const fetchJobById = createAsyncThunk(
  "jobs/fetchJobById",
  async (params: { id: string; include?: string }) => {
    const { id, include } = params;
    const response: JobItem = await jobService.getJobById(id, include);
    return response;
  }
);

// Async thunk to fetch job with detail (work orders, scaffolds, quotes). Does not include scaffold tasks.
export const fetchJobWithDetail = createAsyncThunk(
  "jobs/fetchJobWithDetail",
  async (params: { id: string }) => {
    const { id } = params;
    const response: JobItem = await jobService.getJobWithDetail(id, {
      includeWorkorder: true,
      includeScaffold: true,
      includeQuote: true,
      includeScaffoldTask: false,
    });
    return response;
  }
);

// Async thunk to fetch jobs by client ID
export const fetchJobsByClient = createAsyncThunk(
  "jobs/fetchJobsByClient",
  async (params: {
    clientId: string;
    pageNumber?: number;
    pageSize?: number;
  }) => {
    const { clientId, pageNumber = 1, pageSize = 20 } = params;
    const response: JobsResponse = await jobService.getJobsByClient(
      clientId,
      pageNumber,
      pageSize
    );
    return response;
  }
);

// Async thunk to fetch jobs by site ID
export const fetchJobsBySite = createAsyncThunk(
  "jobs/fetchJobsBySite",
  async (params: {
    siteId: string;
    pageNumber?: number;
    pageSize?: number;
  }) => {
    const { siteId, pageNumber = 1, pageSize = 20 } = params;
    const response: JobsResponse = await jobService.getJobsBySite(
      siteId,
      pageNumber,
      pageSize
    );
    return response;
  }
);

// Async thunk to create a new job
export const createJob = createAsyncThunk(
  "jobs/createJob",
  async (payload: JobCreateRequest) => {
    const response: JobItem = await jobService.createJob(payload);
    return response;
  }
);

// Async thunk to update an existing job
export const updateJob = createAsyncThunk(
  "jobs/updateJob",
  async ({ id, payload }: { id: string; payload: JobUpdateRequest }) => {
    const response: JobItem = await jobService.updateJob(id, payload);
    return response;
  }
);

// Async thunk to fetch job by quote ID
export const fetchJobByQuoteId = createAsyncThunk(
  "jobs/fetchJobByQuoteId",
  async (quoteId: string) => {
    const response: JobItem = await jobService.getJobByQuoteId(quoteId);
    return response;
  }
);

const jobSlice = createSlice({
  name: "Jobs",
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    clearCurrentItem(state) {
      state.currentItem = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch jobs
      .addCase(fetchJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.pageNumber = action.payload.pageNumber;
        state.pageSize = action.payload.pageSize;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch jobs";
      })
      // Fetch job by ID
      .addCase(fetchJobById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentItem = action.payload;
        // Update job in items list if it exists
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(fetchJobById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch job";
      })
      // Fetch job with detail (WithDetail API)
      .addCase(fetchJobWithDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobWithDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentItem = action.payload;
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(fetchJobWithDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch job details";
      })
      // Fetch jobs by client
      .addCase(fetchJobsByClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobsByClient.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.pageNumber = action.payload.pageNumber;
        state.pageSize = action.payload.pageSize;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchJobsByClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch jobs by client";
      })
      // Fetch jobs by site
      .addCase(fetchJobsBySite.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobsBySite.fulfilled, (state, action) => {
        state.loading = false;
        console.log('✅ fetchJobsBySite response:', {
          itemsCount: action.payload.items?.length || 0,
          items: action.payload.items,
          pageNumber: action.payload.pageNumber,
          pageSize: action.payload.pageSize,
          totalCount: action.payload.totalCount,
          totalPages: action.payload.totalPages
        });
        state.items = action.payload.items || [];
        state.pageNumber = action.payload.pageNumber;
        state.pageSize = action.payload.pageSize;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchJobsBySite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch jobs by site";
      })
      // Create job
      .addCase(createJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.loading = false;
        state.currentItem = action.payload;
        // Add to items list if not already present
        const existingIndex = state.items.findIndex(
          (item) => item.id === action.payload.id
        );
        if (existingIndex === -1) {
          state.items.unshift(action.payload);
        } else {
          state.items[existingIndex] = action.payload;
        }
      })
      .addCase(createJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create job";
      })
      // Update job
      .addCase(updateJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateJob.fulfilled, (state, action) => {
        state.loading = false;
        state.currentItem = action.payload;
        // Update item in list if it exists
        const index = state.items.findIndex(
          (item) => item.id === action.payload.id
        );
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update job";
      })
      // Fetch job by quote ID
      .addCase(fetchJobByQuoteId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobByQuoteId.fulfilled, (state, action) => {
        state.loading = false;
        state.currentItem = action.payload;
      })
      .addCase(fetchJobByQuoteId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch job by quote ID";
      });
  },
});

export const { clearError, clearCurrentItem } = jobSlice.actions;
export default jobSlice.reducer;

// Selectors
export const selectJobList = (state: any) => state.Jobs?.items || [];
export const selectCurrentJob = (state: any) => state.Jobs?.currentItem || null;
export const selectJobLoading = (state: any) => state.Jobs?.loading || false;
export const selectJobError = (state: any) => state.Jobs?.error || null;

// Memoized selector for pagination to prevent unnecessary re-renders
export const selectJobPagination = createSelector(
  [
    (state: any) => state.Jobs?.pageNumber ?? 1,
    (state: any) => state.Jobs?.pageSize ?? 20,
    (state: any) => state.Jobs?.totalCount ?? 0,
    (state: any) => state.Jobs?.totalPages ?? 0,
  ],
  (pageNumber, pageSize, totalCount, totalPages) => ({
    pageNumber,
    pageSize,
    totalCount,
    totalPages,
  })
);
